import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Snackbar } from '@mui/material';

import type { UserAdminRequestState } from 'src/types/user';
import { useUserBulkEdit } from 'src/components/userAdmin/hooks/useUserBulkEdit';
import { useUserAdmin } from 'src/components/userAdmin/UserAdminContext';
import { BulkEditDialog } from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditDialog';

export interface UserBulkEditDialogProps {
  selectionModel: number[];
  selectionCount: number;
  downloading: UserAdminRequestState | undefined;
}

const UserBulkEditDialog: FC<UserBulkEditDialogProps> = ({
  selectionModel,
  selectionCount,
  downloading,
}) => {
  const {
    bulkEditDialogOpen,
    setBulkEditDialogOpen,
    searchResults,
    bulkEditingRequestState,
    setBulkEditingRequestState,
  } = useUserAdmin();
  const { handleBulkEditSubmit } = useUserBulkEdit(
    selectionModel,
    setBulkEditingRequestState,
  );
  const { t } = useTranslation('admin');

  return (
    <>
      <BulkEditDialog
        onClose={() => setBulkEditDialogOpen(false)}
        onSubmit={handleBulkEditSubmit}
        open={bulkEditDialogOpen}
        userCount={selectionCount}
        selectedUsers={searchResults.filter((userData) =>
          selectionModel.includes(userData.id),
        )}
      />
      <Snackbar
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        autoHideDuration={6000}
        data-test-id="user-admin-bulk-edit-snackbar"
        onClose={() => setBulkEditingRequestState(undefined)}
        open={
          bulkEditingRequestState?.status === 'success' ||
          bulkEditingRequestState?.status === 'error' ||
          downloading?.status === 'error'
        }
      >
        <Box>
          {bulkEditingRequestState?.status === 'success' && (
            <Alert severity="success">{bulkEditingRequestState?.message}</Alert>
          )}
          {bulkEditingRequestState?.status === 'error' && (
            <Alert severity="error">{bulkEditingRequestState?.message}</Alert>
          )}
          {downloading?.status === 'error' && (
            <Alert severity="error">{t('admin:List.DownloadError')}</Alert>
          )}
        </Box>
      </Snackbar>
    </>
  );
};

export default UserBulkEditDialog;
